/**
 * HintManager - Progressive hint system for all activity types.
 *
 * Ported from bumblebee ChallengeStateMachine. Tracks failures, unlocks hints
 * after threshold, supports 3-tier progressive disclosure, detects frustration
 * and give-up patterns.
 *
 * @see GitHub Issue #852
 */

const FAILURE_THRESHOLD = 2;      // Failures before hints unlock
const FRUSTRATION_RESETS = 3;     // Rapid resets to trigger frustration
const FRUSTRATION_WINDOW_MS = 15000; // Window for rapid reset detection
const GIVE_UP_FAILURES = 8;       // Failures with no improvement = give up

export default class HintManager {
    /**
     * @param {Array} hints - Hint definitions from activity scaffolding
     * @param {object} [callbacks] - {onHintsUnlocked, onFrustration, onGiveUp}
     */
    constructor (hints, callbacks) {
        this._hints = hints || [];
        this._callbacks = callbacks || {};
        this._failures = 0;
        this._hintsUnlocked = false;
        this._currentTier = -1;
        this._resetTimestamps = [];
        this._closestDistance = Infinity;
        this._improvementSinceLastHint = false;
    }

    get hintsUnlocked () {
        return this._hintsUnlocked;
    }

    get currentTier () {
        return this._currentTier;
    }

    get failures () {
        return this._failures;
    }

    get maxTier () {
        return this._hints.length - 1;
    }

    /**
     * Record a failure attempt.
     * @param {number} [distance] - Distance to goal (for challenge type)
     */
    recordFailure (distance) {
        this._failures++;

        // Track closest distance for give-up detection
        if (typeof distance === 'number') {
            if (distance < this._closestDistance) {
                this._closestDistance = distance;
                this._improvementSinceLastHint = true;
            }
        }

        // Check if hints should unlock
        if (!this._hintsUnlocked && this._failures >= FAILURE_THRESHOLD) {
            this._hintsUnlocked = true;
            if (this._callbacks.onHintsUnlocked) {
                this._callbacks.onHintsUnlocked();
            }
        }

        // Check for give-up pattern
        if (this._failures >= GIVE_UP_FAILURES && !this._improvementSinceLastHint) {
            if (this._callbacks.onGiveUp) {
                this._callbacks.onGiveUp();
            }
        }
    }

    /**
     * Record a reset (for frustration detection).
     */
    recordReset () {
        const now = Date.now();
        this._resetTimestamps.push(now);

        // Prune old timestamps outside the window
        const cutoff = now - FRUSTRATION_WINDOW_MS;
        this._resetTimestamps = this._resetTimestamps.filter(t => t >= cutoff);

        // Detect rapid resets
        if (this._resetTimestamps.length >= FRUSTRATION_RESETS) {
            this._resetTimestamps = [];
            if (this._callbacks.onFrustration) {
                this._callbacks.onFrustration();
            }
        }
    }

    /**
     * Get the next available hint.
     * @returns {object|null} Hint object or null if no more hints
     */
    nextHint () {
        if (!this._hintsUnlocked) return null;

        const nextTier = this._currentTier + 1;
        if (nextTier > this.maxTier) return null;

        this._currentTier = nextTier;
        this._improvementSinceLastHint = false;
        return this._hints[nextTier];
    }

    /**
     * Get hint at a specific tier.
     * @param {number} tier
     * @returns {object|null}
     */
    getHint (tier) {
        if (tier < 0 || tier >= this._hints.length) return null;
        return this._hints[tier];
    }

    /**
     * Check if there are more hints available.
     * @returns {boolean}
     */
    hasMoreHints () {
        return this._hintsUnlocked && this._currentTier < this.maxTier;
    }

    /**
     * Reset the hint manager for a new attempt at the same activity.
     */
    resetForRetry () {
        this._currentTier = -1;
        this._improvementSinceLastHint = false;
    }

    /**
     * Full reset for a new activity.
     */
    reset () {
        this._failures = 0;
        this._hintsUnlocked = false;
        this._currentTier = -1;
        this._resetTimestamps = [];
        this._closestDistance = Infinity;
        this._improvementSinceLastHint = false;
    }

    destroy () {
        this._callbacks = {};
        this._hints = [];
        this._resetTimestamps = [];
    }
}
